import React from 'react'
import {
  Icon,
  Typography,
  Button,
  Paper
} from '@telavita-core/react-design-kit'

import { ReactComponent as NoReservationIcon } from 'static/svg/no-reservation-icon.svg'

import { PortalModal } from 'components/PortalModal/PortalModal'

import * as S from './styles'

interface EmptyReservationProps {
  infoDialog: boolean;
  setInfoDialog: (value: boolean) => void;
  allowsAutomaticSchedulingThroughReservation?: boolean;
}

interface InfoDialogProps {
  closeDialog: () => void;
  allowsAutomaticSchedulingThroughReservation?: boolean;
}

const texts = [
  {
    section1: {
      title: 'O que é',
      content: (
        <>
          A reserva foi criada para atender quem deseja fazer cada uma de suas consultas <b>num mesmo dia da semana e horário,</b> de forma recorrente.
        </>
      )
    },
    section2: {
      title: 'Como funciona?',
      content: 'Ao obter uma reserva (por exemplo, para quartas-feiras, às 19h00), você continua agendando suas consultas normalmente. A grande diferença é que este dia e horário estarão garantidos e aparecerão apenas para você, toda vez que for agendar.'
    },
    section3: {
      title: 'Como consigo uma reserva?',
      content: (
        <>
          Basta <b>pedir ao seu psicólogo(a) durante uma consulta,</b> no começo ou um pouco antes dela acabar.
        </>
      )
    },
    section4: {
      content: 'O pedido de reserva está sujeito a indisponibilidade, de acordo com a agenda do profissional. Alguns podem não trabalhar com reserva, também. Pergunte ao seu psicólogo(a).'
    },
    warning: (
      <>
        Ao obter uma reserva, é importante se comprometer a <b>agendar no horário reservado,</b> para evitar o cancelamento da mesma.
      </>
    )
  },
  {
    section1: {
      title: 'O que é uma reserva?',
      content: (
        <>
          A reserva foi criada para garantir que suas consultas aconteçam <b>num mesmo dia da semana e horário,</b> de forma recorrente.
        </>
      )
    },
    section2: {
      title: 'Como funciona?',
      paragraphs: [
        <>
          <b>1)</b> Ao obter uma reserva (por exemplo, para quartas-feiras, às 19h00), você não precisa mais agendar consultas manualmente
        </>,
        <>
          <b>2)</b> Nosso sistema agendará automaticamente as consultas com base nos créditos liberados pelo seu convênio. Ou seja, se forem liberados 5 créditos, agendaremos automaticamente 5 consultas para você.
        </>,
        <>
          <b>3)</b> Futuros agendamentos também serão feitos automaticamente, assim que mais créditos forem liberados.
        </>,
        <>
          <b>4)</b> Mesmo possuindo uma reserva, você continua podendo reagendar uma consulta para um dia e/ou horário diferente, em caso de imprevistos.
        </>
      ]
    },
    section3: {
      title: 'Como consigo uma reserva?',
      paragraphs: [
        <>
          Basta <b>pedir ao seu psicólogo(a) durante uma consulta,</b> no começo ou um pouco antes dela acabar.
        </>,
        'O pedido de reserva está sujeito a indisponibilidade, de acordo com a agenda do profissional. Alguns podem não trabalhar com reserva, também. Pergunte ao seu psicólogo(a).'
      ]
    }
  }
]

const InfoDialog = ({
  closeDialog,
  allowsAutomaticSchedulingThroughReservation = false
}: InfoDialogProps) => {
  const currentText = allowsAutomaticSchedulingThroughReservation
    ? texts[1]
    : texts[0]

  return (
    <PortalModal
      onClose={closeDialog}
      title='Reserva com psicólogo'
      subtitle=''
    >
      <S.InfoDialogContainer>
        <div className="icon">
          <Icon name="NoReservation" />
        </div>
        <div className="container">
          <Typography
            variant="content1"
            weight="bold"
            customClassName="dialogTitle"
          >
            {currentText.section1.title}
          </Typography>

          <Typography variant="content2" customClassName="content">
            {currentText.section1.content}
          </Typography>

          <Typography
            variant="content1"
            weight="bold"
            customClassName="dialogTitle"
          >
            {currentText.section2.title}
          </Typography>

          {currentText.section2.paragraphs ? (
            currentText.section2.paragraphs.map((paragraph, index) => (
              <Typography
                key={index}
                variant="content2"
                customClassName="content"
              >
                {paragraph}
              </Typography>
            ))
          ) : (
            <Typography variant="content2" customClassName="content">
              {currentText.section2.content}
            </Typography>
          )}

          <Typography
            variant="content1"
            weight="bold"
            customClassName="dialogTitle"
          >
            {currentText.section3.title}
          </Typography>

          {currentText.section3.paragraphs ? (
            currentText.section3.paragraphs.map((paragraph, index) => (
              <Typography
                key={index}
                variant="content2"
                customClassName="content"
              >
                {paragraph}
              </Typography>
            ))
          ) : (
            <>
              <Typography variant="content2" customClassName="content">
                {currentText.section3.content}
              </Typography>
              {currentText.section4 && (
                <Typography variant="content2" customClassName="content">
                  {currentText.section4.content}
                </Typography>
              )}
            </>
          )}

          {currentText.warning && (
            <Paper variant="warning" customClassName="paper">
              <Typography variant="content2">
                {currentText.warning}
              </Typography>
            </Paper>
          )}

          <Button
            variant="contained"
            color="primary"
            uppercase={false}
            inlineStyles={{ marginBottom: 40, width: '100%' }}
            onClick={closeDialog}
          >
            Entendi
          </Button>
        </div>
      </S.InfoDialogContainer>
    </PortalModal>
  )
}

export const EmptyReservation = ({
  infoDialog,
  setInfoDialog,
  allowsAutomaticSchedulingThroughReservation = false
}: EmptyReservationProps): JSX.Element => {
  return (
    <>
      <S.EmptyReservationContainer>
        <NoReservationIcon />

        <Typography variant="content2">Nenhuma reserva criada</Typography>

        <Typography variant="content3">
          Garanta o mesmo dia da semana e horário para as suas consultas.
        </Typography>

        <Button
          variant='text'
          uppercase={false}
          onClick={() => setInfoDialog(true)}
          aria-label='Saiba mais sobre a reserva com psicólogo'
          inlineStyles={{
            margin: 0,
            padding: 0,
            height: 'fit-content',
            marginTop: '30px',
          }}
        >
          Saiba mais
        </Button>
      </S.EmptyReservationContainer>

      {infoDialog && (
        <InfoDialog
          closeDialog={() => setInfoDialog(false)}
          allowsAutomaticSchedulingThroughReservation={
            allowsAutomaticSchedulingThroughReservation
          }
        />
      )}
    </>
  )
}
